import React, { useState, useEffect } from 'react';
import PieChartComponent from '@/components/charts/Pie';
import { TotalData, TradingTabKeyNames } from '../../index.type';
import { extractNumber, times100 } from '@/lib/utils';
interface TradingDirectionPreferenceProps {
  selectedMetrics: {
    tradingDirection?: boolean;
    [key: string]: boolean | undefined;
  };
  totalData: Pick<TotalData, TradingTabKeyNames>;
}

interface PieData {
  name: string;
  value: number;
  color: string;
}

interface TradingData {
  max_profit_take: number;
  max_stop_loss: number;
  avg_profit_take: number;
  avg_stop_loss: number;
}

const TradingDirectionPreference: React.FC<TradingDirectionPreferenceProps> = ({
  selectedMetrics,
  totalData
}) => {
  const [currentSelectedMetrics, setCurrentSelectedMetrics] = useState<{
    tradingDirection?: boolean;
    [key: string]: boolean | undefined;
  }>({});

  // 交易数据状态
  const [tradingData, setTradingData] = useState<TradingData>({
    max_profit_take: 0,
    max_stop_loss: 0,
    avg_profit_take: 0,
    avg_stop_loss: 0
  });

  useEffect(() => {
    setCurrentSelectedMetrics(selectedMetrics);
  }, [selectedMetrics]);

  // 模拟接口数据变化
  useEffect(() => {
    const interval = setInterval(() => {}, 3000); // 每3秒更新一次数据

    return () => clearInterval(interval);
  }, []);

  const handleCheckboxChange = (checked: boolean) => {
    setCurrentSelectedMetrics(prev => ({
      ...prev,
      tradingDirection: checked
    }));
  };

  const [pieData, setPieData] = useState<PieData[]>([]);
  useEffect(() => {
    setPieData([
      {
        name: '买入偏好',
        value: Number(extractNumber(totalData.buying_preference_ratio || '0')),
        color: '#13C2C2'
      },
      {
        name: '卖出偏好',
        value: Number(extractNumber(totalData.selling_preference_ratio || '0')),
        color: '#FF7B52'
      }
    ]);

    setTradingData({
      max_profit_take: times100(totalData.max_profit_take || '0').toNumber(),
      max_stop_loss: times100(totalData.max_stop_loss || '0').toNumber(),
      avg_profit_take: times100(totalData.avg_profit_take || '0').toNumber(),
      avg_stop_loss: times100(totalData.avg_stop_loss || '0').toNumber()
    });
  }, [totalData]);

  return (
    <div className="my-4 rounded-lg border border-[#F0F0F0] p-3">
      {/* 标题和复选框 */}
      <div className="mb-3 flex items-center gap-2">
        <input
          type="checkbox"
          checked={currentSelectedMetrics.tradingDirection || false}
          onChange={e => handleCheckboxChange(e.target.checked)}
          className="h-4 w-4"
        />
        <span className="text-base font-semibold text-[#1F1F1F]">
          交易方向偏好
        </span>
      </div>

      {/* 主要内容区域 */}
      <div className="flex items-center gap-10">
        {/* 左侧：饼图和图例 */}
        <div className="flex items-center gap-4">
          <PieChartComponent
            data={pieData}
            width="90px"
            height="90px"
            innerRadius={35}
            outerRadius={45}
            startAngle={90}
            endAngle={450}
            showLegend={true}
            legendPosition="right"
          />
        </div>

        {/* 右侧：指标卡片 */}
        <div className="grid flex-1 gap-3">
          {[
            {
              key: 'maxProfit',
              label: '最大止盈',
              value: `${tradingData.max_profit_take > 0 ? '+' : ''}${tradingData.max_profit_take}%`,
              bgColor: 'bg-[#FFF1F0]',
              borderColor: 'border-[#FFF1F0]',
              textColor: 'text-[#F5222D]'
            },
            {
              key: 'avgProfit',
              label: '平均止盈',
              value: `${tradingData.avg_profit_take > 0 ? '+' : ''}${tradingData.avg_profit_take}%`,
              bgColor: 'bg-[#FFF1F0]',
              borderColor: 'border-[#FFF1F0]',
              textColor: 'text-[#F5222D]'
            },
            {
              key: 'maxLoss',
              label: '最大止损',
              value: `${tradingData.max_stop_loss > 0 ? '+' : ''}${tradingData.max_stop_loss}%`,
              bgColor: 'bg-[#F0FDF4]',
              borderColor: 'border-[#F0FDF4]',
              textColor: 'text-[#059669]'
            },
            {
              key: 'avgLoss',
              label: '平均止损',
              value: `${tradingData.avg_stop_loss > 0 ? '+' : ''}${tradingData.avg_stop_loss}%`,
              bgColor: 'bg-[#F0FDF4]',
              borderColor: 'border-[#F0FDF4]',
              textColor: 'text-[#059669]'
            }
          ].map(item => (
            <div
              key={item.key}
              className={`${item.bgColor} border ${item.borderColor} flex h-8 flex-col justify-between rounded-md p-1`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#1F1F1F]">{item.label}</span>
                <span className={`text-sm font-semibold ${item.textColor}`}>
                  {item.value}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TradingDirectionPreference;
